import React, { useState, useEffect, useRef } from "react";
import "./HomePage.css";

const exercises = [
  { name: "Bicep Curl (Standing)", group: "biceps" },
  { name: "Bicep Curl (Seated)", group: "biceps" },
  { name: "Concentration Curls", group: "biceps" },
  { name: "Hammer Curl (Standing)", group: "biceps" },
  { name: "Hammer Curl (Seated)", group: "biceps" },

  { name: "Inclined Chest Press", group: "chest" },
  { name: "Inclined Chest Fly", group: "chest" },
  { name: "Cable Crossover (Upward)", group: "chest" },
  { name: "Cable Crossover (Downward)", group: "chest" },

  { name: "Lateral Raises", group: "shoulders" },
  { name: "Overhead Press", group: "shoulders" },
  { name: "Upright Rows", group: "shoulders" },
  { name: "Face Pulls", group: "shoulders" },

  { name: "Tricep Extensions", group: "triceps" },
  { name: "Tricep Kickbacks", group: "triceps" },
  { name: "Tricep Pressdown", group: "triceps" },

  { name: "Squats", group: "legs" },
  { name: "Bulgarian Split Squats", group: "legs" },
  { name: "Side Lunges", group: "legs" },
  { name: "Lunges", group: "legs" },
  { name: "Romanian Deadlifts", group: "legs" },
  { name: "Glute Kickbacks", group: "legs" },
  { name: "Hip Thrusts", group: "legs" },

  { name: "Bent Over Rows", group: "back" },
  { name: "Deadlifts", group: "back" },
  { name: "Reverse Flys", group: "back" },

  { name: "Plank", group: "abs" },
  { name: "Side Plank", group: "abs" },
  { name: "Sit-Ups", group: "abs" },
  { name: "Leg Raises", group: "abs" },
  { name: "Hollow Hold", group: "abs" },
];

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const AppStoreBadge = () => (
  <a
    href="https://apps.apple.com/us/app/lift-coach-ai/id6752227972"
    className="app-store-badge"
    target="_blank"
    rel="noopener noreferrer"
  >
    <img
      src="/app_store_download.svg"
      alt="Download on the App Store"
      className="app-store-badge-img"
    />
  </a>
);

const heroImages = [
  { src: "/images/playback.png", alt: "Video replay with overlay" },
  { src: "/images/score.png", alt: "Detailed scoring screen" },
  { src: "/images/setup.png", alt: "Exercise selection screen" },
  { src: "/images/side_plank_setup.png", alt: "Setup for Side Plank" },
];

const HomePage = () => {
  const [shuffledExercises] = useState(() => shuffleArray(exercises));
  const [activeImage, setActiveImage] = useState(0);
  const featureRefs = useRef([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((i) => (i + 1) % heroImages.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const refs = featureRefs.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    refs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      refs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">✦ Now Available on iOS</div>
          <div className="hero-brand">
            <img
              src="/images/app-icon.png"
              alt="Lift Coach AI"
              className="hero-icon"
            />
            <h1>Lift Coach AI</h1>
          </div>
          <h2>A Personal Strength Trainer,{"\n"}in Your Pocket</h2>
          <p>
            Real-time form analysis using cutting-edge computer vision. Get
            instant feedback on every rep in every set.
          </p>
          <AppStoreBadge />
        </div>
        <div className="hero-visual">
          <div className="hero-visual-glow" />
          <div className="hero-mockup-wrapper">
            {heroImages.map((img, i) => (
              <img
                key={img.src}
                src={img.src}
                alt={img.alt}
                className={`hero-mockup${i === activeImage ? " hero-mockup--active" : ""}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <div className="stats-strip">
        <div className="stat">
          <span className="stat-value">30+</span>
          <span className="stat-label">Exercises</span>
        </div>
        <div className="stat">
          <span className="stat-value">Real-Time</span>
          <span className="stat-label">AI Feedback</span>
        </div>
        <div className="stat">
          <span className="stat-value">Free</span>
          <span className="stat-label">on iOS</span>
        </div>
      </div>

      {/* Features Section */}
      <section className="features">
        <p className="section-eyebrow">The Process</p>
        <h2>How It Works</h2>
        <div className="feature-showcase">
          <div
            className="feature-row"
            ref={(el) => (featureRefs.current[0] = el)}
          >
            <div className="feature-content glass-container">
              <div className="feature-image">
                <img src="/images/setup.png" alt="Exercise selection screen" />
              </div>
              <div className="feature-text">
                <h3>1. Choose Your Exercise</h3>
                <p>
                  Select from our growing library of exercises. Position your
                  phone properly, and you will be guided to an appropriate setup
                  position before you begin.
                </p>
              </div>
            </div>
          </div>

          <div
            className="feature-row reverse"
            ref={(el) => (featureRefs.current[1] = el)}
          >
            <div className="feature-content glass-container">
              <div className="feature-text">
                <h3>2. Get Instant Feedback</h3>
                <p>
                  Start your reps and receive instant audio feedback. Lift Coach
                  AI counts your reps and alerts you to issues as they happen —
                  no need to look at your screen.
                </p>
              </div>
              <div className="feature-image">
                <img
                  src="/images/reps.png"
                  alt="Audio feedback during workout"
                />
              </div>
            </div>
          </div>

          <div
            className="feature-row"
            ref={(el) => (featureRefs.current[2] = el)}
          >
            <div className="feature-content glass-container">
              <div className="feature-image">
                <img src="/images/score.png" alt="Detailed scoring screen" />
              </div>
              <div className="feature-text">
                <h3>3. Check Your Score</h3>
                <p>
                  Get a score after each set with personalized tips for
                  improvement. Review specific reps and track your progress over
                  time.
                </p>
              </div>
            </div>
          </div>

          <div
            className="feature-row reverse"
            ref={(el) => (featureRefs.current[3] = el)}
          >
            <div className="feature-content glass-container">
              <div className="feature-text">
                <h3>4. Replay Your Set</h3>
                <p>
                  Watch your recorded sets with visual guidance showing exactly
                  where to improve. Replay specific reps and review the feedback
                  you received during your set.
                </p>
              </div>
              <div className="feature-image">
                <img
                  src="/images/playback.png"
                  alt="Video replay with overlay"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exercise Library Carousel */}
      <section className="exercise-library">
        <p className="section-eyebrow">Exercise Library</p>
        <h2>Support for a Variety of Exercises</h2>
        <div className="carousel-container">
          <div className="exercise-carousel">
            {[...shuffledExercises, ...shuffledExercises].map(
              (exercise, index) => (
                <div
                  key={`${exercise.name}-${index}`}
                  className={`exercise-card ${exercise.group}`}
                >
                  <span>{exercise.name}</span>
                </div>
              ),
            )}
          </div>
        </div>
        <p className="carousel-note">More exercises are added frequently.</p>
      </section>

      {/* Footer CTA */}
      <section className="footer-cta">
        <div className="footer-cta-inner glass-container">
          <h2>Get Lift Coach AI Today</h2>
          <p>Download Lift Coach AI for free today on the App Store.</p>
          <AppStoreBadge />
          <div>
            <a href="mailto:support@getliftcoach.com" className="support-link">
              Email Support
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
